"use client"

import { EmoNeutral, UserFilled, Zoom } from "@assets"
import { Head, router } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"

interface Conversation {
  id: number
  title?: string
  participant: {
    id: number
    name: string
    avatar_url?: string
    is_online?: boolean
    last_seen?: string
  }
  last_message?: {
    content: string
    created_at: string
    is_from_current_user: boolean
  }
  is_group?: boolean
  group_name?: string
  group_members_count?: number
  unread_count?: number
}

interface Props {
  conversations: Conversation[]
}

export default function ChatIndex({ conversations }: Props) {
  const [searchTerm, setSearchTerm] = useState("")
  const [, setShowNewChatDialog] = useState(false)
  const [userSearchTerm] = useState("")
  const [, setSearchResults] = useState<
    { id: number; name: string; avatar_url?: string }[]
  >([])
  const [, setIsSearching] = useState(false)

  // Search users for new chat
  useEffect(() => {
    if (userSearchTerm.length < 2) {
      setSearchResults([])
      return
    }

    const searchUsers = async () => {
      setIsSearching(true)
      try {
        const res = await fetch(
          `/usuarios/buscar?q=${encodeURIComponent(userSearchTerm)}`,
          {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
          },
        )
        if (res.ok) {
          const data = (await res.json()) as {
            users: { id: number; name: string; avatar_url?: string }[]
          }
          setSearchResults(data.users || [])
        }
      } catch {
        // no-op
      } finally {
        setIsSearching(false)
      }
    }

    const timeout = setTimeout(() => {
      void searchUsers()
    }, 300)
    return () => clearTimeout(timeout)
  }, [userSearchTerm])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMinutes / 60)

    if (diffInMinutes < 1) return "ahora"
    if (diffInMinutes < 60) return `${diffInMinutes}m`
    if (diffInHours < 14) return `${diffInHours}h`

    return date.toLocaleDateString("es-ES", {
      month: "short",
      day: "numeric",
    })
  }

  const getOnlineStatus = (participant: { is_online?: boolean }) => {
    if (participant.is_online) {
      return (
        <div className="absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
      )
    }
    return null
  }

  const filteredConversations = conversations.filter((conv) => {
    const q = searchTerm.toLowerCase()
    const title = (
      conv.title ??
      conv.group_name ??
      conv.participant.name ??
      ""
    ).toLowerCase()
    return title.includes(q)
  })

  const selectConversation = (conversationId: number) => {
    router.get(`/chat/${conversationId}`)
  }

  return (
    <AppLayout
      title="Mensajes"
      onCreateChat={() => setShowNewChatDialog(false)}
    >
      <Head title="Mensajes" />

      {/* Conversations (mobile styles only) */}
      <div className="mx-auto max-w-6xl space-y-3 p-[12px] md:p-6">
        {/* Search bar */}
        <div className="bg-card flex h-[44px] w-full items-end rounded-[12px]">
          <Zoom className="text-primary bg-card mx-[10px] my-[6px] h-[27px] fill-current" />
          <Input
            placeholder="Buscar mensajes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="placeholder:text-muted-foreground w-full border-0 px-0 pt-0 pb-[6px] text-base font-light tracking-[0.07px] opacity-[0.75] shadow-[0_0_0_0px_rgba(0,0,0,0)] focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Conversations List */}
        <div className="mt-[22px]">
          {filteredConversations.length > 0 ? (
            <div className="divide-y divide-white/10">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => selectConversation(conversation.id)}
                  className="hover:bg-card/10 active:bg-card/20 cursor-pointer pb-[14px] transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative flex-shrink-0">
                      <Avatar className="h-[56px] w-[56px] border-2 border-white">
                        <AvatarImage
                          src={conversation.participant.avatar_url}
                        />
                        <AvatarFallback className="bg-modal p-1">
                          <UserFilled className="fill-chart-2 h-full w-full" />
                        </AvatarFallback>
                      </Avatar>
                      {getOnlineStatus(conversation.participant)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-chart-5 truncate font-['Roboto_Condensed'] text-[18px] leading-[22px] font-medium tracking-[0.18px]">
                        {conversation.title ??
                          conversation.group_name ??
                          conversation.participant.name}
                      </h3>
                      <div className="mt-0.5 flex items-center gap-2 text-center">
                        {conversation.last_message && (
                          <p className="text-chart-5 truncate text-[15px] leading-[23px] tracking-[0.06px]">
                            {conversation.last_message.is_from_current_user &&
                              "Tú: "}
                            {conversation.last_message.content}
                          </p>
                        )}
                        {conversation.last_message && (
                          <span className="text-muted-foreground ml-2 flex-shrink-0 text-[13px] leading-[20px] tracking-[0.07px]">
                            <span className="mx-1">•</span>
                            {formatDate(conversation.last_message.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {(conversation.unread_count ?? 0) > 0 && (
                      <Badge className="bg-primary text-primary-foreground flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full text-xs">
                        {conversation.unread_count ?? 0}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="-mx-[12px] mt-[20px] flex h-[245px] flex-col items-center justify-center text-center"
              style={{ background: "var(--gradient-highlight)" }}
            >
              <div className="mb-[5px] flex h-[56px] w-[56px] items-center justify-center">
                <img src={EmoNeutral} alt="Sin contenido" />
              </div>

              <h3 className="text-center text-[18px] font-semibold tracking-[0px] sm:text-xl">
                No se encontraron conversaciones
              </h3>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
